import { calendarOf, text } from '#contract'
import type { LocalizedText } from '#contract'

/** 2024-01-01 was a Monday: day `n` after it is weekday `n`. */
const A_MONDAY = Date.UTC(2024, 0, 1)
const DAY_MS = 24 * 60 * 60 * 1000

const weekdays = new Map<string, readonly string[]>()

/** The short weekday name (`Mon`) in `locale`; `weekday` 0–6 is Monday first. */
export function weekdayName(locale: string, weekday: number): string {
  let names = weekdays.get(locale)
  if (!names) {
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
    names = Array.from({ length: 7 }, (_, i) => format.format(A_MONDAY + i * DAY_MS))
    weekdays.set(locale, names)
  }
  return names[weekday] ?? ''
}

/** The game day as the HUD shows it: "Mon · Week 1". */
export function dayText(locale: string, day: number): LocalizedText {
  const { weekday, week } = calendarOf(day)
  return text('calendar.day', {
    weekday: weekdayName(locale, weekday),
    week: text('calendar.week', { n: week }),
  })
}

/** When a save was made, in the player's time zone: "20 Oct, 14:02". */
export function savedAtText(locale: string, savedAt: number): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(savedAt)
}

/** Real time played: "2 h 10 min", or "12 min". */
export function playTimeText(seconds: number): LocalizedText {
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  return hours > 0
    ? text('time.hoursMinutes', { h: hours, m: minutes % 60 })
    : text('time.minutes', { m: minutes })
}
